/**
 * "Companies Management" — pagina master CRUD ragioni sociali cross-tenant.
 * Replica il pattern Round 4 stores: gating bottoni via viewer_permissions
 * (permessi tenant_companies.*), lifecycle sospendi/archivia/riattiva/elimina
 * solo se vuota (409 COMPANY_NOT_EMPTY con elenco riferimenti FK).
 */
import { useState, useEffect, useCallback } from 'react';
import AnimatedPage from '@/components/AnimatedPage';
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@easyflow/ui-master/components/ui/table';
import { SkeletonMetricGrid, SkeletonTable } from '@easyflow/ui-master/components/ui/skeleton-loader';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@easyflow/ui-master/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@easyflow/ui-master/components/ui/alert-dialog';
import {
  FileText, Building2, RefreshCw, Search, Activity, Landmark,
  Plus, MoreHorizontal, Pencil, PauseCircle, Archive, PlayCircle, Trash2, PauseOctagon,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import apiClient, { ApiRequestError } from '@/lib/api';
import { toErr } from '@easyflow/shared';
import { CompanyFormModalMaster, type MasterCompanyDetail } from '@/components/CompanyFormModalMaster';

type CompanyStatus = 'active' | 'suspended' | 'archived';

interface CompanyData {
  id: string;
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  tenant_status: string;
  legal_name: string;
  vat_number: string | null;
  tax_code: string | null;
  subject_type: string | null;
  legal_form: string | null;
  tax_regime: string | null;
  city: string | null;
  state: string | null;
  pec_email: string | null;
  sdi_code: string | null;
  fiscal_provider: string | null;
  status: CompanyStatus;
  /** Task #3563 — governance brand: org che gestisce la RS (NULL = tenant-managed). */
  managed_by_org_id: string | null;
  stores_count: number;
  invoices_count: number;
  created_at: string;
  updated_at: string;
}

interface CompanyStats {
  total_companies: number;
  active_companies: number;
  suspended_companies: number;
  archived_companies: number;
  tenants_with_companies: number;
  with_fiscal_provider: number;
}

interface ViewerPermissions {
  can_create: boolean;
  can_update: boolean;
  can_lifecycle: boolean;
}

interface CompaniesListData {
  companies: CompanyData[];
  viewer_permissions: ViewerPermissions;
}

interface CompanyReference {
  table: string;
  count: number;
}

const noPermissions: ViewerPermissions = {
  can_create: false,
  can_update: false,
  can_lifecycle: false,
};

type ConfirmKind = 'suspend' | 'archive' | 'reactivate' | 'delete';

interface ConfirmState {
  kind: ConfirmKind;
  company: CompanyData;
}

const CONFIRM_COPY: Record<ConfirmKind, { title: string; description: string; action: string; destructive: boolean }> = {
  suspend: {
    title: 'Sospendere la ragione sociale?',
    description: 'Resterà visibile nel tenant con badge "Sospesa" e le azioni di modifica e le operazioni fiscali saranno bloccate.',
    action: 'Sospendi',
    destructive: false,
  },
  archive: {
    title: 'Archiviare la ragione sociale?',
    description: 'Sparirà dalle liste del tenant. Potrà essere riattivata solo dal master.',
    action: 'Archivia',
    destructive: false,
  },
  reactivate: {
    title: 'Riattivare la ragione sociale?',
    description: 'Tornerà attiva e pienamente operativa nel tenant.',
    action: 'Riattiva',
    destructive: false,
  },
  delete: {
    title: 'Eliminare definitivamente la ragione sociale?',
    description: 'Operazione irreversibile, consentita solo se la ragione sociale non ha dati collegati (sedi, fatture, sezionali, ecc.).',
    action: 'Elimina',
    destructive: true,
  },
};

const SUBJECT_TYPE_LABELS: Record<string, string> = {
  company: 'Società',
  sole_proprietorship: 'Ditta individuale',
  freelancer: 'Libero professionista',
};

function statusBadge(status: CompanyStatus) {
  switch (status) {
    case 'active':
      return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">Attiva</Badge>;
    case 'suspended':
      return <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">Sospesa</Badge>;
    case 'archived':
      return <Badge className="bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300">Archiviata</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

const NATIVE_SELECT_CLASS =
  'px-3 py-2 border border-border dark:border-slate-600 rounded-md bg-white dark:bg-muted text-foreground dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-cyan-500';

export default function CompaniesMaster() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [companies, setCompanies] = useState<CompanyData[]>([]);
  const [stats, setStats] = useState<CompanyStats | null>(null);
  const [perms, setPerms] = useState<ViewerPermissions>(noPermissions);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | CompanyStatus>('all');
  const [tenantFilter, setTenantFilter] = useState<string>('all');
  // Modale create/edit.
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<MasterCompanyDetail | null>(null);
  // Conferme lifecycle.
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Esito DELETE bloccata: riferimenti che impediscono l'eliminazione.
  const [blockedRefs, setBlockedRefs] = useState<{ companyName: string; references: CompanyReference[] } | null>(null);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [listRes, statsRes] = await Promise.all([
        apiClient.get<CompaniesListData>('/master/companies'),
        apiClient.get<CompanyStats>('/master/companies/stats'),
      ]);
      setCompanies(listRes.data?.companies ?? []);
      setPerms(listRes.data?.viewer_permissions ?? noPermissions);
      setStats(statsRes.data ?? null);
    } catch (error: unknown) {
      toast({
        title: 'Errore',
        description: toErr(error).message || 'Caricamento ragioni sociali fallito',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Tenant distinti presenti in lista, per il filtro.
  const tenantOptions = [...new Map(companies.map((c) => [c.tenant_id, { id: c.tenant_id, name: c.tenant_name, slug: c.tenant_slug }])).values()]
    .sort((a, b) => a.name.localeCompare(b.name));

  const filteredCompanies = companies.filter((c) => {
    if (tenantFilter !== 'all' && c.tenant_id !== tenantFilter) return false;
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    const search = searchTerm.toLowerCase();
    return c.legal_name.toLowerCase().includes(search) ||
      (c.vat_number || '').toLowerCase().includes(search) ||
      (c.tax_code || '').toLowerCase().includes(search) ||
      c.tenant_name.toLowerCase().includes(search) ||
      (c.city || '').toLowerCase().includes(search);
  });

  const openCreate = (): void => {
    setEditingCompany(null);
    setIsModalOpen(true);
  };

  const openEdit = async (company: CompanyData): Promise<void> => {
    setBusyId(company.id);
    try {
      const res = await apiClient.get<MasterCompanyDetail>(`/master/companies/${company.id}`);
      if (!res.data) throw new Error('Ragione sociale non trovata');
      setEditingCompany(res.data);
      setIsModalOpen(true);
    } catch (err) {
      toast({ title: 'Errore', description: toErr(err).message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const runLifecycle = async ({ kind, company }: ConfirmState): Promise<void> => {
    setBusyId(company.id);
    try {
      if (kind === 'delete') {
        await apiClient.delete(`/master/companies/${company.id}`);
        toast({ title: 'Ragione sociale eliminata', description: `"${company.legal_name}" è stata eliminata definitivamente.` });
      } else {
        const path = kind === 'suspend' ? 'suspend' : kind === 'archive' ? 'archive' : 'reactivate';
        await apiClient.post(`/master/companies/${company.id}/${path}`);
        const label = kind === 'suspend' ? 'sospesa' : kind === 'archive' ? 'archiviata' : 'riattivata';
        toast({ title: 'Operazione completata', description: `Ragione sociale "${company.legal_name}" ${label}.` });
      }
      await loadData();
    } catch (err: unknown) {
      const body = err instanceof ApiRequestError
        ? (err.body as { code?: string; data?: { references?: CompanyReference[] } } | null)
        : null;
      if (kind === 'delete' && body?.code === 'COMPANY_NOT_EMPTY' && Array.isArray(body.data?.references)) {
        setBlockedRefs({ companyName: company.legal_name, references: body.data.references });
      } else {
        toast({ title: 'Errore', description: toErr(err).message, variant: 'destructive' });
      }
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="mb-6">
          <div className="h-10 w-96 bg-white/20 rounded animate-pulse mb-2" />
          <div className="h-4 w-128 bg-white/10 rounded animate-pulse" />
        </div>
        <SkeletonMetricGrid cols={4} />
        <SkeletonTable rows={10} />
      </div>
    );
  }

  const canActOn = perms.can_update || perms.can_lifecycle;

  return (
    <AnimatedPage>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Companies Management
            </h1>
            <p className="text-muted-foreground mt-1">Gestione cross-tenant delle ragioni sociali</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => void loadData()} variant="outline">
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            {perms.can_create && (
              <Button onClick={openCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Nuova ragione sociale
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <GlassCard className="border-l-4 border-l-blue-500">
              <GlassCardHeader className="pb-3">
                <GlassCardDescription className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  RS totali
                </GlassCardDescription>
                <GlassCardTitle className="text-3xl">{stats.total_companies}</GlassCardTitle>
              </GlassCardHeader>
            </GlassCard>

            <GlassCard className="border-l-4 border-l-green-500">
              <GlassCardHeader className="pb-3">
                <GlassCardDescription className="flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Attive
                </GlassCardDescription>
                <GlassCardTitle className="text-3xl text-green-600">{stats.active_companies}</GlassCardTitle>
              </GlassCardHeader>
            </GlassCard>

            <GlassCard className="border-l-4 border-l-amber-500">
              <GlassCardHeader className="pb-3">
                <GlassCardDescription className="flex items-center gap-2">
                  <PauseOctagon className="h-4 w-4" />
                  Sospese / Archiviate
                </GlassCardDescription>
                <GlassCardTitle className="text-3xl text-amber-600">
                  {stats.suspended_companies} / {stats.archived_companies}
                </GlassCardTitle>
              </GlassCardHeader>
            </GlassCard>

            <GlassCard className="border-l-4 border-l-purple-500">
              <GlassCardHeader className="pb-3">
                <GlassCardDescription className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Tenant con RS
                </GlassCardDescription>
                <GlassCardTitle className="text-3xl">{stats.tenants_with_companies}</GlassCardTitle>
              </GlassCardHeader>
            </GlassCard>

            <GlassCard className="border-l-4 border-l-orange-500">
              <GlassCardHeader className="pb-3">
                <GlassCardDescription className="flex items-center gap-2">
                  <Landmark className="h-4 w-4" />
                  Con provider fiscale
                </GlassCardDescription>
                <GlassCardTitle className="text-3xl text-orange-600">{stats.with_fiscal_provider}</GlassCardTitle>
              </GlassCardHeader>
            </GlassCard>
          </div>
        )}

        {/* Companies Table */}
        <GlassCard>
          <GlassCardHeader>
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70 h-4 w-4" />
                <Input
                  placeholder="Cerca per denominazione, P.IVA, CF, tenant, città..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={tenantFilter}
                onChange={(e) => setTenantFilter(e.target.value)}
                className={`w-48 ${NATIVE_SELECT_CLASS}`}
              >
                <option value="all">Tutti i tenant</option>
                {tenantOptions.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | CompanyStatus)}
                className={`w-40 ${NATIVE_SELECT_CLASS}`}
              >
                <option value="all">Tutti gli stati</option>
                <option value="active">Attive</option>
                <option value="suspended">Sospese</option>
                <option value="archived">Archiviate</option>
              </select>
            </div>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ragione sociale</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Fiscale</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="text-center">Sedi / Fatture</TableHead>
                    <TableHead>Creata</TableHead>
                    {canActOn && <TableHead className="text-right">Azioni</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCompanies.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canActOn ? 8 : 7} className="text-center py-8 text-muted-foreground">
                        Nessuna ragione sociale trovata
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCompanies.map((c) => (
                      <TableRow key={c.id} className="hover:bg-muted/50 dark:hover:bg-white/5">
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-muted-foreground/70" />
                            <div>
                              <div>{c.legal_name}</div>
                              <div className="text-xs text-muted-foreground font-mono">
                                {[c.vat_number && `P.IVA ${c.vat_number}`, c.tax_code && `CF ${c.tax_code}`].filter(Boolean).join(' · ') || '—'}
                              </div>
                              {c.city && (
                                <div className="text-xs text-muted-foreground">
                                  {[c.city, c.state].filter(Boolean).join(' ')}
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{c.tenant_name}</div>
                            <div className="text-sm text-muted-foreground">{c.tenant_slug}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{SUBJECT_TYPE_LABELS[c.subject_type ?? ''] ?? '—'}</div>
                          {c.tax_regime && (
                            <div className="text-xs text-muted-foreground">{c.tax_regime}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          {c.fiscal_provider && c.fiscal_provider !== 'none' ? (
                            <Badge variant="outline" className="uppercase text-[10px]">{c.fiscal_provider}</Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                          {c.sdi_code && (
                            <div className="text-xs text-muted-foreground font-mono mt-0.5">SDI {c.sdi_code}</div>
                          )}
                        </TableCell>
                        <TableCell>{statusBadge(c.status)}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex flex-col items-center">
                            <span className="font-semibold">{c.stores_count}</span>
                            <span className="text-xs text-muted-foreground">/ {c.invoices_count} fatture</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {new Date(c.created_at).toLocaleDateString()}
                          </span>
                        </TableCell>
                        {canActOn && (
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" disabled={busyId === c.id} aria-label="Azioni ragione sociale">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {perms.can_update && (
                                  <DropdownMenuItem onClick={() => void openEdit(c)}>
                                    <Pencil className="h-4 w-4 mr-2" /> Modifica
                                  </DropdownMenuItem>
                                )}
                                {perms.can_lifecycle && (
                                  <>
                                    {perms.can_update && <DropdownMenuSeparator />}
                                    {c.status === 'active' && (
                                      <DropdownMenuItem onClick={() => setConfirm({ kind: 'suspend', company: c })}>
                                        <PauseCircle className="h-4 w-4 mr-2" /> Sospendi
                                      </DropdownMenuItem>
                                    )}
                                    {c.status !== 'archived' && (
                                      <DropdownMenuItem onClick={() => setConfirm({ kind: 'archive', company: c })}>
                                        <Archive className="h-4 w-4 mr-2" /> Archivia
                                      </DropdownMenuItem>
                                    )}
                                    {(c.status === 'suspended' || c.status === 'archived') && (
                                      <DropdownMenuItem onClick={() => setConfirm({ kind: 'reactivate', company: c })}>
                                        <PlayCircle className="h-4 w-4 mr-2" /> Riattiva
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="text-destructive focus:text-destructive"
                                      onClick={() => setConfirm({ kind: 'delete', company: c })}
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" /> Elimina (solo se vuota)
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </GlassCardContent>
        </GlassCard>

        {/* Modale create/edit */}
        <CompanyFormModalMaster
          isOpen={isModalOpen}
          onClose={() => { setIsModalOpen(false); setEditingCompany(null); }}
          onSuccess={() => void loadData()}
          company={editingCompany}
        />

        {/* Conferma lifecycle */}
        <AlertDialog open={confirm !== null} onOpenChange={(open) => { if (!open) setConfirm(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{confirm ? CONFIRM_COPY[confirm.kind].title : ''}</AlertDialogTitle>
              <AlertDialogDescription>
                {confirm && (
                  <>
                    <span className="font-medium text-foreground">"{confirm.company.legal_name}"</span>
                    {' '}({confirm.company.tenant_name}) — {CONFIRM_COPY[confirm.kind].description}
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annulla</AlertDialogCancel>
              <AlertDialogAction
                className={confirm && CONFIRM_COPY[confirm.kind].destructive
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : undefined}
                onClick={() => { if (confirm) void runLifecycle(confirm); }}
              >
                {confirm ? CONFIRM_COPY[confirm.kind].action : ''}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Eliminazione bloccata: elenco riferimenti */}
        <AlertDialog open={blockedRefs !== null} onOpenChange={(open) => { if (!open) setBlockedRefs(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Eliminazione non consentita</AlertDialogTitle>
              <AlertDialogDescription>
                La ragione sociale <span className="font-medium text-foreground">"{blockedRefs?.companyName}"</span> ha
                dati collegati che ne impediscono l'eliminazione:
              </AlertDialogDescription>
            </AlertDialogHeader>
            {blockedRefs && (
              <ul className="text-sm space-y-1 max-h-48 overflow-y-auto">
                {blockedRefs.references.map((r) => (
                  <li key={r.table} className="flex justify-between border-b border-border/50 py-1">
                    <code className="text-xs">{r.table}</code>
                    <span className="text-muted-foreground">{r.count >= 1000 ? '1000+' : r.count} righe</span>
                  </li>
                ))}
              </ul>
            )}
            <AlertDialogFooter>
              <AlertDialogAction onClick={() => setBlockedRefs(null)}>Ho capito</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AnimatedPage>
  );
}
